import * as React from 'react'
import { ActiveTab } from '@/app/constants/tab'
import { FaIcon } from '@/lib/fa'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

export type TabItemProps = {
  name: string
  active?: boolean
  disabled?: boolean
  type: ActiveTab
  invalid?: boolean
  onSelected?: (item: TabItemProps) => void
  stickers?: Array<string>
  icon?: IconDefinition | string // FontAwesome icon or SVG path
}

export const TabItem: React.FC<TabItemProps> = (props) => {
  const {
    name,
    active = false,
    disabled = false,
    invalid = false,
    stickers,
    onSelected,
    type,
    icon,
  } = props
  const { t } = useLocalize()

  const handleClick = () => {
    if (!disabled && onSelected) {
      onSelected(props)
    }
  }

  const getTabClasses = () => {
    const baseClasses = 'tw-relative tw-cursor-pointer tw-text-nowrap tw-p-4 tw-text-sm tw-font-medium tw-transition-colors tw-flex tw-items-center tw-gap-2'
    
    if (disabled) {
      return `${baseClasses} tw-opacity-50 tw-cursor-not-allowed`
    }

    if (invalid) {
      if (active) {
        return `${baseClasses} tw-border-b-[2px] tw-border-red-500 tw-font-medium tw-text-red-600 tw-bg-red-100`
      }
      return `${baseClasses} tw-border-b-[2px] tw-border-red-300 tw-text-content-base tw-bg-red-50 hover:tw-bg-red-100`
    }

    if (active) {
      return `${baseClasses} tw-border-b-[2px] tw-border-blue-600 tw-font-medium tw-text-blue-600`
    }

    return `${baseClasses} tw-border-b-[2px] tw-border-transparent tw-text-content-base hover:tw-text-blue-600`
  }

  const renderIcon = () => {
    if (!icon) return null

    // Check if icon is a string (SVG path) or IconDefinition (FontAwesome)
    if (typeof icon === 'string') {
      return (
        <img
          src={icon}
          alt=""
          className={`tw-w-4 tw-h-4 ${active ? '' : 'tw-opacity-70'}`}
        />
      )
    }

    // FontAwesome icon
    return (
      <FaIcon
        icon={icon}
        className={`tw-w-4 tw-h-4 ${active ? '' : 'tw-opacity-70'}`}
      />
    )
  }

  return (
    <div
      className={getTabClasses()}
      onClick={handleClick}
    >
      {renderIcon()}
      <span>{name}</span>
      
      {stickers && stickers.length > 0 && (
        <div className="tw-absolute tw-top-1 tw-right-1 tw-flex tw-gap-0.5">
          {stickers.map((value, i) => (
            <span
              key={i}
              className="badge badge-xs tw-text-white"
              style={{
                backgroundColor: i === 0 ? '#2B2E33' : '#FF6A88',
              }}
            >
              {value}
            </span>
          ))}
        </div>
      )}
      
      {invalid && (
        <div className="tw-absolute tw-top-1 tw-left-1">
          <img src="/assets/icons/del_warning.svg" alt="warning" />
        </div>
      )}
    </div>
  )
}

